import { useRef, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface ImageRevealProps {
    src: string
    alt: string
    className?: string
    wrapperClassName?: string
}

const REVEAL_STEPS = [
    { label: 'Encrypted...', color: '#444' },
    { label: 'Scanning...', color: '#666' },
    { label: 'Reconstructing...', color: '#888' },
    { label: 'Rendering...', color: '#aaa' },
    { label: '✓ Verified', color: '#00FF88' },
]

const STEP_DURATION = 220 // ms per step — total ~1.1s

export default function ImageReveal({ src, alt, className = '', wrapperClassName = '' }: ImageRevealProps) {
    const prefersReduced = typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false

    const wrapRef = useRef<HTMLDivElement>(null)
    const [inView, setInView] = useState(false)
    const [step, setStep] = useState(prefersReduced ? REVEAL_STEPS.length : 0) // 0 = encrypted, 5 = done
    const triggered = useRef(false)

    /* Observe once when in viewport */
    useEffect(() => {
        if (prefersReduced) return
        const el = wrapRef.current
        if (!el) return

        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !triggered.current) {
                    triggered.current = true
                    setInView(true)
                    obs.disconnect()
                }
            },
            { threshold: 0.2 }
        )
        obs.observe(el)
        return () => obs.disconnect()
    }, [prefersReduced])

    /* Step through reveal sequence */
    useEffect(() => {
        if (!inView || prefersReduced) return
        if (step >= REVEAL_STEPS.length) return

        const t = setTimeout(() => setStep(s => s + 1), STEP_DURATION)
        return () => clearTimeout(t)
    }, [inView, step, prefersReduced])

    const isRevealed = step >= REVEAL_STEPS.length
    const currentStep = REVEAL_STEPS[Math.min(step, REVEAL_STEPS.length - 1)]

    return (
        <div ref={wrapRef} className={`img-reveal-wrapper ${wrapperClassName}`}>
            {/* Actual image — fades in when revealed */}
            <AnimatePresence>
                {isRevealed && (
                    <motion.img
                        key="img"
                        src={src}
                        alt={alt}
                        className={className}
                        initial={{ opacity: 0, filter: 'brightness(3) saturate(0)' }}
                        animate={{ opacity: 1, filter: 'brightness(1) saturate(1)' }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                        loading="lazy"
                    />
                )}
            </AnimatePresence>

            {/* Overlay: encrypted → verified steps */}
            {!isRevealed && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#0a0a0a] border border-[#1a1a1a]">
                    {/* Scan sweep line */}
                    {inView && (
                        <div
                            className="absolute left-0 right-0 h-px pointer-events-none z-10"
                            style={{
                                background: 'linear-gradient(90deg, transparent, #00FF88, transparent)',
                                top: `${(step / REVEAL_STEPS.length) * 100}%`,
                                transition: 'top 200ms linear',
                                opacity: 0.7,
                            }}
                        />
                    )}

                    {/* Step label */}
                    <p
                        className="text-[10px] font-mono animate-enc-blink"
                        style={{ color: currentStep?.color ?? '#444' }}
                    >
                        {inView ? currentStep?.label : REVEAL_STEPS[0].label}
                    </p>

                    {/* Progress track */}
                    <div className="w-24 h-px bg-[#111]">
                        <div
                            className="h-full bg-primary transition-all duration-200"
                            style={{ width: `${(step / REVEAL_STEPS.length) * 100}%` }}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
